import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../Context/ThemeContext';
import { PromotionManagementHandler, PromotionListItem } from './PromotionManagementHandler';
import PromotionFormModal from './PromotionFormModal';

const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const PromotionListPage: React.FC = () => {
    const { theme, isMobile } = useTheme();

    const [promotions, setPromotions] = useState<PromotionListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    const [deleteConfirm, setDeleteConfirm] = useState<PromotionListItem | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchPromotions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const handler = new PromotionManagementHandler();
            const response = await handler.getPromotions();
            setPromotions(response?.results || []);
        } catch (err) {
            console.error('Error fetching promotions:', err);
            setError('Failed to load promotions.');
            setPromotions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPromotions();
    }, [fetchPromotions]);

    const handleAdd = () => {
        setEditingId(null);
        setShowModal(true);
    };

    const handleEdit = (id: string) => {
        setEditingId(id);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditingId(null);
    };

    const handleModalSaved = () => {
        setShowModal(false);
        setEditingId(null);
        fetchPromotions();
    };

    const handleDeleteExecute = async () => {
        if (!deleteConfirm?.id) return;
        setDeleteLoading(true);
        try {
            const handler = new PromotionManagementHandler();
            await handler.deletePromotion(deleteConfirm.id);
            setDeleteConfirm(null);
            fetchPromotions();
        } catch (err: any) {
            console.error('Error deleting promotion:', err);
            setError(err?.response?.data?.message || err?.message || 'Failed to delete promotion.');
            setDeleteConfirm(null);
        } finally {
            setDeleteLoading(false);
        }
    };

    const styles: Record<string, React.CSSProperties> = {
        container: { padding: 0 },
        headerRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            flexWrap: 'wrap' as const,
            gap: '12px',
        },
        title: {
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 700,
            color: theme.titleColor,
            marginBottom: '4px',
        },
        subtitle: { fontSize: '14px', color: theme.textSecondary },
        addButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#fff',
            background: '#3b82f6',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
        },
        tableContainer: {
            background: theme.card,
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        tableWrapper: { overflowX: 'auto' as const },
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            tableLayout: 'fixed' as const,
            minWidth: isMobile ? '760px' : 'auto',
        },
        th: {
            padding: '14px 16px',
            textAlign: 'center' as const,
            fontWeight: 600,
            fontSize: '13px',
            color: theme.textSecondary,
            background: theme.tableHeader,
            borderBottom: `1px solid ${theme.border}`,
            whiteSpace: 'nowrap' as const,
        },
        td: {
            padding: '14px 16px',
            textAlign: 'center' as const,
            fontSize: '14px',
            color: theme.text,
            borderBottom: `1px solid ${theme.border}`,
        },
        actionButton: {
            padding: '5px 10px',
            fontSize: '12px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '6px',
        },
        badge: {
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 600,
        },
        spinner: {
            width: '24px',
            height: '24px',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
        loadingContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 0',
            gap: '16px',
        },
        errorBanner: {
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#ef4444',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        emptyRow: {
            textAlign: 'center' as const,
            padding: '40px',
            color: theme.textSecondary,
            fontStyle: 'italic' as const,
        },
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        confirmModal: {
            background: theme.card,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
        confirmTitle: { fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '12px' },
        confirmText: { fontSize: '14px', color: theme.textSecondary, marginBottom: '20px', lineHeight: 1.5 },
        confirmActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px' },
    };

    const statusBadge = (active: boolean): React.CSSProperties =>
        active
            ? { ...styles.badge, background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }
            : { ...styles.badge, background: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };

    return (
        <div style={styles.container}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

            <div style={styles.headerRow}>
                <div>
                    <h1 style={styles.title}>Promotions</h1>
                    <p style={styles.subtitle}>Manage the promotions shown in the website&apos;s promotions bell</p>
                </div>
                <button
                    style={styles.addButton}
                    onClick={handleAdd}
                    onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#2563eb'; }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.background = '#3b82f6'; }}
                >
                    + Add Promotion
                </button>
            </div>

            {error && (
                <div style={styles.errorBanner}>
                    <span>{error}</span>
                    <button
                        onClick={fetchPromotions}
                        style={{ padding: '4px 12px', fontSize: '13px', fontWeight: 600, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        Retry
                    </button>
                </div>
            )}

            <div style={styles.tableContainer}>
                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: '26%' }}>Title</th>
                                <th style={{ ...styles.th, width: '18%' }}>Reward</th>
                                <th style={{ ...styles.th, width: '9%' }}>Status</th>
                                <th style={{ ...styles.th, width: '8%' }}>Priority</th>
                                <th style={{ ...styles.th, width: '13%' }}>Ends</th>
                                <th style={{ ...styles.th, width: '10%' }}>Languages</th>
                                <th style={{ ...styles.th, width: '16%' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} style={styles.td}>
                                        <div style={styles.loadingContainer}>
                                            <div style={styles.spinner} />
                                            <span style={{ color: theme.textSecondary }}>Loading promotions...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : promotions.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ ...styles.td, ...styles.emptyRow }}>
                                        No promotions found. Click &quot;Add Promotion&quot; to create one.
                                    </td>
                                </tr>
                            ) : (
                                promotions.map((promo) => (
                                    <tr
                                        key={promo.id}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = theme.tableRowHover; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        <td style={{ ...styles.td, fontWeight: 600, textAlign: 'left' }}>{promo.title || '-'}</td>
                                        <td style={{ ...styles.td, color: theme.textSecondary, fontSize: '13px' }}>{promo.reward || '-'}</td>
                                        <td style={styles.td}>
                                            <span style={statusBadge(promo.active)}>{promo.active ? 'Active' : 'Hidden'}</span>
                                        </td>
                                        <td style={styles.td}>{promo.priority}</td>
                                        <td style={styles.td}>{formatDate(promo.endDate)}</td>
                                        <td style={styles.td}>{promo.languageCount}</td>
                                        <td style={styles.td}>
                                            <button
                                                style={{ ...styles.actionButton, background: '#3b82f6', color: '#fff' }}
                                                onClick={() => handleEdit(promo.id)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                style={{ ...styles.actionButton, background: '#ef4444', color: '#fff' }}
                                                onClick={() => setDeleteConfirm(promo)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <PromotionFormModal
                    promotionId={editingId}
                    onClose={handleModalClose}
                    onSaved={handleModalSaved}
                />
            )}

            {deleteConfirm && (
                <div style={styles.overlay} onClick={() => !deleteLoading && setDeleteConfirm(null)}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.confirmTitle}>Delete Promotion</div>
                        <div style={styles.confirmText}>
                            Are you sure you want to delete <strong>&quot;{deleteConfirm.title}&quot;</strong>?
                            This action cannot be undone and it will immediately stop showing on the website.
                        </div>
                        <div style={styles.confirmActions}>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.text,
                                    background: 'transparent',
                                    border: `1px solid ${theme.border}`,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setDeleteConfirm(null)}
                                disabled={deleteLoading}
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#fff',
                                    background: '#ef4444',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    opacity: deleteLoading ? 0.6 : 1,
                                }}
                                onClick={handleDeleteExecute}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? 'Deleting...' : 'Delete Promotion'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PromotionListPage;
